import type { AnalyticEventName } from './analytics';

const scopes = ['reporter', 'viewer', 'worklist', 'search', 'workspace'] as const;
const types = ['sys', 'usr'] as const;

export type MetricScope = (typeof scopes)[number];
export type MetricType = (typeof types)[number];

export type MetricOpts = {
  scope: MetricScope;
  type: MetricType;
  name: string;
};

/**
 * Used to make dynamic metrics for a certain scope and type.
 */
export const makeMetric = ({ scope, type, name }: MetricOpts): string => `${scope}.${type}.${name}`;

/**
 * Validates a metric name has the proper naming convention of "scope.type.name" and
 * additionally can validate that a particular scope and type are used.
 */
export const isMetric = (
  name: AnalyticEventName,
  opts?: {
    scope?: MetricScope;
    type?: MetricType;
  }
): boolean => {
  const parts = name?.split('.') ?? [];
  if (parts.length !== 3) return false;
  const [scope, type, metricName] = parts;
  if (!scopes.includes(scope as MetricScope)) return false;
  if (!types.includes(type as MetricType)) return false;
  if (metricName.length === 0) return false;
  if (opts != null) {
    if (opts.scope != null && opts.scope !== scope) return false;
    if (opts.type != null && opts.type !== type) return false;
  }
  return true;
};

/*
 * Metrics are grouped by component area, as well as by sys and usr.
 * usr metrics are meant for user actions, e.g. button clicked.
 * sys metrics are meant for system actions/info, e.g. websocket opened.
 */

export const viewer = {
  sys: {
    dicomSeriesCacheHit: 'viewer.sys.dicom_series_cache_hit',
    dicomSeriesDownload: 'viewer.sys.dicom_series_download',
    nrrdMaskCacheHit: 'viewer.sys.nrrd_mask_cache_hit',
    nrrdMaskDownload: 'viewer.sys.nrrd_mask_download',
    externalIntegrationError: 'viewer.sys.external_integration_error',
    externalIntegrationSuccess: 'viewer.sys.external_integration_success',
    externalViewCaseError: 'viewer.sys.external_view_case_error',
    externalViewCaseSuccess: 'viewer.sys.external_view_case_success',
    renderMemory: 'viewer.sys.render_memory',
    renderMetadata: 'viewer.sys.render_metadata',
    allInteractable: 'viewer.sys.all_interactable',
    slowFrame: 'viewer.sys.slow_frame',
    viewportRendering: 'viewer.sys.viewport_rendering',
    webglContextLost: 'viewer.sys.webgl_context_lost',
    annotationSaved: 'viewer.sys.annotation_saved',
    annotationsRendered: 'viewer.sys.annotations_rendered',
    pixelWorkerLoadStack: 'viewer.sys.pixel_worker_load_stack',
    pixelWorkerLoadComplete: 'viewer.sys.pixel_worker_load_complete',
    pixelWorkerLoadFrameOrigin: 'viewer.sys.pixel_worker_load_frame_origin',
    pixelWorkerConnectionOpened: 'viewer.sys.pixel_worker_connection_opened',
    pixelWorkerConnectionClosed: 'viewer.sys.pixel_worker_connection_closed',
    pixelWorkerLruEvicted: 'viewer.sys.pixel_worker_lru_evicted',
    // VX-4302: emitted on every worker-generation bump (automatic or manual). "fires rarely" is the
    // design's core safety claim, so ops needs visibility on EVERY bump, not just failed incidents.
    escapeHatchBump: 'viewer.sys.escape_hatch_bump',
    // VX-4754: the verdict on each bump — did frame delivery actually resume? A bump that fixes
    // nothing used to be indistinguishable from one that recovered in two seconds, so a failed
    // self-heal reported healthy. Ops alerts on `outcome: 'unrecovered'`.
    escapeHatchBumpOutcome: 'viewer.sys.escape_hatch_bump_outcome',
    precacheRefetchDiff: 'viewer.sys.precache_refetch_diff',
    // VX-3280: emitted when a viewport's picking gate opens (data ready). The gate failing silently
    // for MIP/Volume viewports was the bug, so a regression shows as this going to zero for a view type.
    viewportPickingEnabled: 'viewer.sys.viewport_picking_enabled',
  },
  usr: {
    averageScrollingFrameDuration: 'viewer.usr.average_scrolling_frame_duration',
    seriesDropped: 'viewer.usr.series_dropped',
    scrolledToLoadingSlice: 'viewer.usr.scrolled_to_loading_slice',
    hangingProtocolCreated: 'viewer.usr.hanging_protocol_created',
    hangingProtocolUpdated: 'viewer.usr.hanging_protocol_updated',
    hangingProtocolDeleted: 'viewer.usr.hanging_protocol_deleted',
    hangingProtocolApplied: 'viewer.usr.hanging_protocol_applied',
    hangingProtocolStudyDescriptionEdited: 'viewer.usr.hanging_protocol_study_description_edited',
    hangingProtocolNameEdited: 'viewer.usr.hanging_protocol_name_edited',
    hangingProtocolSeriesDescriptionEdited: 'viewer.usr.hanging_protocol_series_description_edited',
    hangingProtocolViewTypeChanged: 'viewer.usr.hanging_protocol_view_type_changed',
    hangingProtocolClearViewportToggled: 'viewer.usr.hanging_protocol_clear_viewport_toggled',
    hangingProtocolStudyIndexChanged: 'viewer.usr.hanging_protocol_study_index_changed',
    hangingProtocolLayoutChanged: 'viewer.usr.hanging_protocol_layout_changed',
    seriesDroppedBeforeFirstRecording: 'viewer.usr.series_dropped_before_first_recording',
  },
} as const;

export const reporter = {
  sys: {
    audioCueStartLatency: 'reporter.sys.audio_cue_start_latency',
    audioCueStopLatency: 'reporter.sys.audio_cue_stop_latency',
    dictationDuration: 'reporter.sys.dictation_duration',
    recordingStartRecorderStartedLatency: 'reporter.sys.recording_start_recorder_started_latency',
    recordingStartUiPaintedLatency: 'reporter.sys.recording_start_ui_painted_latency',
    recordingStopRecorderStoppedLatency: 'reporter.sys.recording_stop_recorder_stopped_latency',
    recordingStopUiPaintedLatency: 'reporter.sys.recording_stop_ui_painted_latency',
    dictationQueued: 'reporter.sys.dictation_queued',
    dictationDevices: 'reporter.sys.dictation_devices',
    dictaphoneProductMatchEvaluated: 'reporter.sys.dictaphone_product_match_evaluated',
    mixPanelIdentifyUser: 'reporter.sys.mixpanel_identify_user',
    mixPanelResetUser: 'reporter.sys.mixpanel_reset_user',
    nvoqHypothesisStableGap: 'reporter.sys.nvoq_hypothesis_stable_gap',
    nvoqTimeToFirstStableText: 'reporter.sys.nvoq_time_to_first_stable_text',
    nvoqTimeToLastStableText: 'reporter.sys.nvoq_time_to_last_stable_text',
    nvoqWebSocketTimeToConnect: 'reporter.sys.nvoq_websocket_time_to_connect',
    nvoqWebSocketTimeConnected: 'reporter.sys.nvoq_websocket_time_connected',
    nvoqWebSocketTimeCreated: 'reporter.sys.nvoq_websocket_time_created',
    slowAudioProcessing: 'reporter.sys.slow_audio_processing',
    stableTextReceived: 'reporter.sys.stable_text_received',
    picklistOptionSelectionResolved: 'reporter.sys.picklist_option_selection_resolved',
    textCorrectedByDictation: 'reporter.sys.text_corrected_by_dictation',
    textNormalized: 'reporter.sys.text_normalized',
    upgradeHeadingKeywordToHeading: 'reporter.sys.upgrade_heading_keyword_to_heading',
    renderMemory: 'reporter.sys.render_memory',
    focusModeSessionDuration: 'reporter.sys.focus_mode_session_duration',
    focusModeMacroResolution: 'reporter.sys.focus_mode_macro_resolution',
    rightDrawerTableSpinnerLong: 'reporter.sys.right_drawer_table_spinner_long',
    rightDrawerTablePaginationLong: 'reporter.sys.right_drawer_table_pagination_long',
  },
  usr: {
    recordingStarted: 'reporter.usr.recording_started',
    recordingStopped: 'reporter.usr.recording_stopped',
    controlInputActionTriggered: 'reporter.usr.control_input_action_triggered',
    controlInputConnected: 'reporter.usr.control_input_connected',
    controlInputDisconnected: 'reporter.usr.control_input_disconnected',
    controlInputWrongMode: 'reporter.usr.control_input_wrong_mode',
    audioInputSelected: 'reporter.usr.audio_input_selected',
    starRating: 'reporter.usr.star_rating',
    reportExitAction: 'reporter.usr.report_exit_action',
    submitReport: 'reporter.usr.submit_report',
    submitLinkedCases: 'reporter.usr.submit_linked_cases',
    submitReportCancel: 'reporter.usr.submit_report_cancel',
    submitPrelimReport: 'reporter.usr.submit_prelim_report',
    submitPrelimReportCancel: 'reporter.usr.submit_prelim_report_cancel',
    submitAddendum: 'reporter.usr.submit_addendum',
    submitAddendumCancel: 'reporter.usr.submit_addendum_cancel',
    voiceCommand: 'reporter.usr.voice_command',
    wordsDictated: 'reporter.usr.words_dictated',
    charactersTyped: 'reporter.usr.characters_typed',
    markApplied: 'reporter.usr.mark_applied',
    clearMarksForSelection: 'reporter.usr.clear_marks_for_selection',
    insertPlaceholder: 'reporter.usr.insert_placeholder',
    submitPriorSummaryFeedback: 'reporter.usr.submit_prior_summary_feedback',
    insertTemplate: 'reporter.usr.insert_template',
    insertTemplateTriggerToFetch: 'reporter.usr.insert_template_trigger_to_fetch',
    insertTemplateFetchToEdit: 'reporter.usr.insert_template_fetch_to_edit',
    insertTemplateEditToScreen: 'reporter.usr.insert_template_edit_to_screen',
    insertTemplateTotal: 'reporter.usr.insert_template_total',
    insertMacroTriggerToFetch: 'reporter.usr.insert_macro_trigger_to_fetch',
    insertMacroFetchToEdit: 'reporter.usr.insert_macro_fetch_to_edit',
    insertMacroEditToScreen: 'reporter.usr.insert_macro_edit_to_screen',
    insertMacroTotal: 'reporter.usr.insert_macro_total',
    insertPickListTriggerToEdit: 'reporter.usr.insert_pick_list_trigger_to_edit',
    insertPickListEditToScreen: 'reporter.usr.insert_pick_list_edit_to_screen',
    insertPickListTotal: 'reporter.usr.insert_pick_list_total',
    reportNavigation: 'reporter.usr.report_navigation',
    autoloadNextCase: 'reporter.usr.autoload_next_case',
    autoImpressionRendered: 'reporter.usr.auto_impression_rendered',
    focusModeToggled: 'reporter.usr.focus_mode_toggled',
    focusModeMacroInserted: 'reporter.usr.focus_mode_macro_inserted',
  },
} as const;

export const worklist = {
  sys: {
    renderMemory: 'worklist.sys.render_memory',
    loadRequestFired: 'worklist.sys.load_time_to_request_fired',
    loadDataReady: 'worklist.sys.load_time_to_data_ready',
    loadPaint: 'worklist.sys.load_time_to_paint',
    paginationRequestFired: 'worklist.sys.pagination_time_to_request_fired',
    paginationDataReady: 'worklist.sys.pagination_time_to_data_ready',
    paginationPaint: 'worklist.sys.pagination_time_to_paint',
    patientJacketWorklistItemFetch: 'worklist.sys.patient_jacket_worklist_item_fetch',
    patientJacketSelectedPriorLoad: 'worklist.sys.patient_jacket_selected_prior_load',
    patientJacketSelectedPriorPaint: 'worklist.sys.patient_jacket_selected_prior_paint',
    patientJacketPaint: 'worklist.sys.patient_jacket_paint',
    patientJacketPriorsSummaryLoad: 'worklist.sys.patient_jacket_priors_summary_load',
    patientJacketPriorsSummaryPaint: 'worklist.sys.patient_jacket_priors_summary_paint',
    crudAbandon: 'worklist.sys.crud_abandon',
    crudSubmit: 'worklist.sys.crud_submit',
    crudSave: 'worklist.sys.crud_save',
    crudDelete: 'worklist.sys.crud_delete',
    columnResize: 'worklist.sys.column_resize',
    columnResizeSave: 'worklist.sys.column_resize_save',
    adjustColumns: 'worklist.sys.adjust_columns',
    adjustColumnsPaint: 'worklist.sys.adjust_columns_paint',
    columnPin: 'worklist.sys.column_pin',
    columnPinPaint: 'worklist.sys.column_pin_paint',
    columnReorder: 'worklist.sys.column_reorder',
    columnReorderPaint: 'worklist.sys.column_reorder_paint',
    drawerOpenPaint: 'worklist.sys.drawer_open_paint',
  },
  usr: {
    // Actions that change an exam's status
    addToQueue: 'worklist.usr.add_to_queue',
    batchAddToQueue: 'worklist.usr.batch_add_to_queue',
    removeFromQueue: 'worklist.usr.remove_from_queue',
    batchRemoveFromQueue: 'worklist.usr.batch_remove_from_queue',
    moveToQC: 'worklist.usr.move_to_qc',
    verifyCase: 'worklist.usr.verify',
    viewCase: 'worklist.usr.view_case',
    readCase: 'worklist.usr.read_case',
    transferCase: 'worklist.usr.transfer_case',
    startAssignCase: 'worklist.usr.start_assign_case',
    assignCase: 'worklist.usr.assign_case',

    // Any actions that happen in the worklist
    selectCase: 'worklist.usr.select_case',
    deselectCase: 'worklist.usr.deselect_case',
    selectAllCases: 'worklist.usr.select_all_cases',
    deselectAllCases: 'worklist.usr.deselect_all_cases',
    sort: 'worklist.usr.sort',
    search: 'worklist.usr.search',
    loadNextPage: 'worklist.usr.load_next_page',
    loadDefaultWorklist: 'worklist.usr.load_default_worklist',

    // Actions related to filter usage
    filtersOpen: 'worklist.usr.filters_open',
    filtersApply: 'worklist.usr.filters_apply',
    filtersClose: 'worklist.usr.filters_close',
    filtersReset: 'worklist.usr.filters_reset',
    filtersResetAll: 'worklist.usr.filters_reset_all',

    // Actions in the left drawer of the custom worklist
    leftDrawerToggle: 'worklist.usr.left_drawer_toggle',
    leftDrawerSectionToggle: 'worklist.usr.left_drawer_section_toggle',
    leftDrawerSearch: 'worklist.usr.left_drawer_search',
    leftDrawerResize: 'worklist.usr.left_drawer_resize',
    leftDrawerMenuOptions: 'worklist.usr.left_drawer_menu_options',

    // Actions related to the active worklist
    viewAppliedFiltersTooltip: 'worklist.usr.view_applied_filters_tooltip',
    viewAdditionalFiltersTooltip: 'worklist.usr.view_additional_filters_tooltip',
    clickWorklist: 'worklist.usr.click_worklist',
    combineWorklist: 'worklist.usr.combine_worklist',
    removeWorklist: 'worklist.usr.remove_worklist',

    // Patient jacket actions
    transitionPatientJacket: 'worklist.usr.transition_patient_jacket',
    openDockedPatientJacket: 'worklist.usr.open_docked_patient_jacket',
    openUndockedPatientJacket: 'worklist.usr.open_undocked_patient_jacket',
    closePatientJacket: 'worklist.usr.close_patient_jacket',
    dockPatientJacket: 'worklist.usr.dock_patient_jacket',
    undockPatientJacket: 'worklist.usr.undock_patient_jacket',
    patientJacketSelectRow: 'worklist.usr.patient_jacket_select_row',
    patientJacketAddPrior: 'worklist.usr.patient_jacket_add_prior',
    patientJacketRemovePrior: 'worklist.usr.patient_jacket_remove_prior',
    patientJacketViewCurrentExam: 'worklist.usr.patient_jacket_view_current_exam',
    patientJacketResizePanel: 'worklist.usr.patient_jacket_resize_panel',
    patientJacketTogglePanel: 'worklist.usr.patient_jacket_toggle_panel',
    patientJacketToggleTab: 'worklist.usr.patient_jacket_toggle_tab',
    patientJacketToggleRelevantPriors: 'worklist.usr.patient_jacket_toggle_relevant_priors',

    // Misc actions
    downloadReport: 'worklist.usr.download_report',
    resendReport: 'worklist.usr.resend_report',
    downloadDicom: 'worklist.usr.download_dicom',
    copyLink: 'worklist.usr.copy_link',
    viewHistory: 'worklist.usr.view_history',
    switchHistoryTab: 'worklist.usr.switch_history_tab',
    changeCriticalResult: 'worklist.usr.change_critical_result',

    // Custom worklist actions
    openWorklistDialog: 'worklist.usr.open_worklist_dialog',
    closeWorklistDialog: 'worklist.usr.close_worklist_dialog',
    saveWorklistDialog: 'worklist.usr.save_worklist_dialog',
    cancelWorklistDialog: 'worklist.usr.cancel_worklist_dialog',
    startDeleteWorklist: 'worklist.usr.start_delete_worklist',
    deleteWorklist: 'worklist.usr.delete_worklist',
    cancelDeleteWorklist: 'worklist.usr.cancel_delete_worklist',
    editWorklist: 'worklist.usr.edit_worklist',
    createWorklist: 'worklist.usr.create_worklist',
    cloneWorklist: 'worklist.usr.clone_worklist',
    startEditWorklist: 'worklist.usr.start_edit_worklist',
    startCreateWorklist: 'worklist.usr.start_create_worklist',
    startCloneWorklist: 'worklist.usr.start_clone_worklist',
  },
} as const;

export const search = {
  sys: {
    searchJacketPaint: 'search.sys.search_jacket_paint',
    searchJacketWorklistItemFetch: 'search.sys.search_jacket_worklist_item_fetch',
    searchJacketSelectedPriorLoad: 'search.sys.search_jacket_selected_prior_load',
    searchJacketSelectedPriorPaint: 'search.sys.search_jacket_selected_prior_paint',
    searchJacketPriorsSummaryLoad: 'search.sys.search_jacket_priors_summary_load',
    searchJacketPriorsSummaryPaint: 'search.sys.search_jacket_priors_summary_paint',
  },
  usr: {
    searchJacketSelectRow: 'search.usr.search_jacket_select_row',
    searchJacketAddPrior: 'search.usr.search_jacket_add_prior',
    searchJacketRemovePrior: 'search.usr.search_jacket_remove_prior',
    searchJacketViewCurrentExam: 'search.usr.search_jacket_view_current_exam',
    searchJacketResizePanel: 'search.usr.search_jacket_resize_panel',
    searchJacketTogglePanel: 'search.usr.search_jacket_toggle_panel',
    searchJacketToggleTab: 'search.usr.search_jacket_toggle_tab',
    searchJacketToggleRelevantPriors: 'search.usr.search_jacket_toggle_relevant_priors',
    downloadReport: 'search.usr.download_report',
    downloadDicom: 'search.usr.download_dicom',
    resendReport: 'search.usr.resend_report',
    copyLink: 'search.usr.copy_link',
    switchHistoryTab: 'search.usr.switch_history_tab',
    viewHistory: 'search.usr.view_history',
    startAssignCase: 'search.usr.start_assign_case',
  },
} as const;

export const workspace = {
  sys: {
    system: 'workspace.sys.system',
    browserStorageReset: 'workspace.sys.browser_storage_reset',
  },
  usr: {
    login: 'workspace.usr.login',
    clearCase: 'workspace.usr.clear_case',
    openBlankRoute: 'workspace.usr.open_blank_route',
  },
} as const;

export const globalContext = {
  workspace: {
    sironaPreview: 'workspace.sirona_preview',
    sironaStandalone: 'workspace.sirona.session_started_as',
    permissions: (permission: string) => `workspace.permissions.${permission}`,
    featureFlag: (feature: string) => `workspace.feature_flags.${feature}`,
    storageEstimate: 'workspace.storage_estimate',
    extensionVersion: 'workspace.extension_version',
    clusterConnection: 'workspace.cluster_connection',
  },
  viewer: {
    layout: 'viewer.layout',
    windowId: 'viewer.window_id',
    tool: 'viewer.active_tool',
    windowsOpen: 'viewer.windows_open',
    openWindowsIds: 'viewer.open_windows_ids',
    numberOfRelevantPriors: 'viewer.number_of_relevant_priors',
    totalNumberOfFrames: 'viewer.total_number_of_frames',
    totalNumberOfPixels: 'viewer.total_number_of_pixels',
    pixelsPerStack: 'viewer.pixels_per_stack',
    viewportConfigurations: 'viewer.viewport_configurations',
    urlPixelLoaderOriginCacheStatus: 'viewer.url_pixel_loader_origin_cache_status',
    urlPixelLoaderOriginCacheService: 'viewer.url_pixel_loader_origin_cache_service',
    precacheStatus: 'viewer.precache_status',
    memoryInfo: {
      totalCapacityMb: 'viewer.memory.total_capacity_mb',
      source: 'viewer.memory.source',
    },
    loadMetrics: {
      hangedFramesPerSecond: 'viewer.load_metrics.hanged_frames_per_second',
      unhangedFramesPerSecond: 'viewer.load_metrics.unhanged_frames_per_second',
      allFramesPerSecond: 'viewer.load_metrics.all_frames_per_second',
    },
    windowState: 'viewer.window_state',
    systemInfo: 'viewer.system_info',
    pixelMemory: 'viewer.pixel_memory',
  },
  patient: {
    hl7Modality: 'patient.hl7_modality',
    sscModality: 'patient.modality',
    bodyPart: 'patient.body_part',
  },
  worklistSettings: {
    autoLoad: 'worklist_settings.auto_load',
  },
  window: {
    desyncEvent: 'window.desync_event',
    spamClickEvent: 'window.spam_click_event',
  },
  page: {
    fromRefresh: 'page.from_refresh',
  },
  browser: {
    fullVersion: 'browser.full_version',
  },
} as const;

/**
 * Custom timing names sent to Datadog RUM via analytics.timing()
 * These are used to track performance metrics throughout the application.
 */
export const CUSTOM_TIMINGS = {
  /** React app initial render complete */
  reactAppRendered: 'react_app_rendered',
  viewer: {
    /** Viewer container component rendered */
    containerRendered: 'viewer_container_rendered',
    /** Hanging protocol successfully loaded */
    hangingProtocolLoadComplete: 'viewer_hanging_protocol_load_complete',
    /** All viewports finished initial render */
    viewportsInitialRenderFinished: 'viewer_viewports_initial_render_finished',
    /** All viewports are interactable */
    viewportsInteractable: 'viewer_viewports_interactable',
    /** Annotations finished rendering */
    annotationsRenderComplete: 'viewer_annotations_render_complete',
    workerImagingProvider: {
      /** Worker started loading a stack */
      loadStack: 'viewer_ws_imaging_provider_load_stack',
      /** Worker completed loading a stack */
      loadComplete: 'viewer_ws_imaging_provider_load_complete',
      /** Worker completed loading all stacks */
      allLoadsComplete: 'viewer_ws_imaging_provider_all_loads_complete',
      /** Worker completed loading all unhanged stacks */
      unhangedLoadsComplete: 'viewer_ws_imaging_provider_unhanged_loads_complete',
      /** Worker handled initial frame */
      handleInitialFrame: 'viewer_ws_imaging_provider_handle_initial_frame',
      /** Worker completed 25% of initial loads */
      loads25PercentComplete: 'viewer_ws_imaging_provider_loads_25_percent_complete',
    },
  },
} as const;
